import { useQuery } from "@tanstack/react-query";
import {
    AlertTriangle,
    Ban,
    Download,
    FileSpreadsheet,
    FileText,
    FileX,
    Info,
    LoaderCircle,
    RefreshCw,
    RotateCcw,
} from "lucide-react";
import {
    type FC,
    type ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import { documentInfoQueryOptions } from "@/api";
import { isOutputDocumentMetadataNotFoundError } from "@/api/documents";
import { isAxiosNotFoundError } from "@/api/errors";
import type { ITemplateRenderSummary } from "@/api/template-renders/template-renders.types";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useOutputDocumentDownload } from "@/hooks/useOutputDocumentDownload";
import {
    LIVE_STATUSES,
    useRetryRender,
    useTemplateRenders,
} from "@/hooks/useTemplateRenders";
import { cn } from "@/lib/utils";
import { useDocumentViewer } from "@/pages/frontend/chat/contexts/useDocumentViewer";

const ADMIN_ACTION_COPY = {
    NO_READY_VERSION:
        "It needs a prepared version before it can be filled. Ask an admin to prepare it, then try again.",
    BINDING_INVALID:
        "It needs to be reconnected to this assistant. Ask an admin to reconnect it, then try again.",
    UNSUPPORTED_ONTOLOGY_VERSION:
        "It has to be recompiled before it can be filled. Ask an admin to prepare it, then try again.",
} as const;

const NON_RETRYABLE_FAILURE_COPY = {
    OUTPUT_PACKAGE_INTEGRITY_MISMATCH: {
        title: "We couldn't verify this document",
        detail: "The filled file may be incomplete. Contact support before using it.",
        treatment: "contact-support",
    },
    ONTOLOGY_LAYOUT_MISMATCH: {
        title: "This template needs attention",
        detail: "Ask an admin to recompile this template before trying again.",
        treatment: "recompile",
    },
    SOURCE_PACKAGE_MALFORMED: {
        title: "This template needs attention",
        detail: "The source file could not be read. Ask an admin to replace it.",
        treatment: "replace-source",
    },
    FILL_PLAN_BUDGET_EXCEEDED: {
        title: "This document has too much data",
        detail: "Reduce the number of rows to fill, then run the template again.",
        treatment: "reduce-input",
    },
} as const;

type AdminActionCode = keyof typeof ADMIN_ACTION_COPY;
type NonRetryableFailureCode = keyof typeof NON_RETRYABLE_FAILURE_COPY;
type DocumentAvailability = "loading" | "available" | "recoverable" | "removed";

function isAdminActionCode(
    failureCode: string | null
): failureCode is AdminActionCode {
    return failureCode !== null && failureCode in ADMIN_ACTION_COPY;
}

function isNonRetryableFailureCode(
    failureCode: string | null
): failureCode is NonRetryableFailureCode {
    return failureCode !== null && failureCode in NON_RETRYABLE_FAILURE_COPY;
}

const Title: FC<{ children: ReactNode; className?: string }> = ({
    children,
    className,
}) => (
    <p
        className={cn(
            "text-foreground text-sm leading-tight font-semibold",
            className
        )}
    >
        {children}
    </p>
);

const Detail: FC<{ children: string; testId?: string }> = ({
    children,
    testId,
}) => (
    <p
        data-testid={testId}
        className="text-muted-foreground text-[0.8125rem] leading-snug"
    >
        {children}
    </p>
);

const IconTile: FC<{
    children: ReactNode;
    className?: string;
}> = ({ children, className }) => (
    <span
        className={cn(
            "flex size-[2.375rem] shrink-0 items-center justify-center rounded-[0.625rem]",
            className
        )}
        aria-hidden
    >
        {children}
    </span>
);

interface TemplateRenderCardViewProps {
    render: ITemplateRenderSummary;
    documentName: string | null;
    documentAvailability: DocumentAvailability;
    isDownloading: boolean;
    isResolving: boolean;
    isRetrying: boolean;
    onDownload: () => void;
    onOpen: () => void;
    onResolve: () => void;
    onRetry: () => void;
}

export const TemplateRenderCardView: FC<TemplateRenderCardViewProps> = ({
    render,
    documentName,
    documentAvailability,
    isDownloading,
    isResolving,
    isRetrying,
    onDownload,
    onOpen,
    onResolve,
    onRetry,
}) => {
    const { status } = render;
    const isWorking = LIVE_STATUSES.has(status);
    const fileType = (render.file_type ?? "pdf").toLowerCase();
    const spreadsheet = fileType !== "pdf";

    if (
        !isWorking &&
        status !== "completed" &&
        status !== "skipped" &&
        status !== "failed"
    ) {
        return null;
    }

    if (isWorking) {
        return (
            <Card
                data-testid="template-render-card"
                role="group"
                aria-label="Filled document"
                className="border-primary/20 bg-primary/[0.04] dark:border-primary/25 dark:bg-primary/[0.08] mt-3 flex-row items-start gap-3 border p-3.5 ring-0"
            >
                <IconTile className="bg-primary/10 text-primary">
                    <LoaderCircle className="size-[1.125rem] animate-spin motion-reduce:animate-none" />
                </IconTile>
                <div
                    data-testid="template-render-processing"
                    role="status"
                    aria-live="polite"
                    className="min-w-0 flex-1"
                >
                    <Title>Filling your document</Title>
                    <Detail>
                        Pulling answers from this conversation — this only takes
                        a moment.
                    </Detail>
                    <div
                        data-testid="template-render-indeterminate"
                        data-state="indeterminate"
                        className="bg-primary/15 mt-2.5 h-1 overflow-hidden rounded-full"
                        aria-hidden
                    >
                        <div
                            data-testid="template-render-indeterminate-bar"
                            className="bg-primary h-full w-2/5 animate-[template-compile-indeterminate_1.3s_ease-in-out_infinite] rounded-full will-change-transform motion-reduce:animate-none"
                        />
                    </div>
                </div>
            </Card>
        );
    }

    const completedIcon = spreadsheet ? (
        <FileSpreadsheet className="size-[1.1875rem]" />
    ) : (
        <FileText className="size-[1.1875rem]" />
    );
    const completedIconClass = spreadsheet
        ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
        : "bg-destructive/10 text-destructive";
    const fileTypeLabel = spreadsheet ? "Spreadsheet" : "PDF";

    if (status === "completed" && documentAvailability === "removed") {
        return (
            <Card
                data-testid="template-render-card"
                role="group"
                aria-label="Filled document"
                className="bg-muted mt-3 flex-row items-start gap-3 border p-3.5 ring-0"
            >
                <IconTile className="border-border bg-card text-muted-foreground border">
                    <FileX className="size-[1.125rem]" />
                </IconTile>
                <div className="min-w-0 flex-1">
                    <Title className="text-muted-foreground">
                        This document was removed
                    </Title>
                    <Detail testId="template-render-removed">
                        It's no longer available to open. Re-run the template to
                        create a fresh copy.
                    </Detail>
                </div>
            </Card>
        );
    }

    if (status === "completed") {
        const loading = documentAvailability === "loading";
        const recoverable = documentAvailability === "recoverable";
        const available = !loading && !recoverable;

        return (
            <Card
                data-testid="template-render-card"
                data-file-type={fileType}
                role="group"
                aria-label="Filled document"
                className={cn(
                    "border-primary/20 bg-primary/[0.04] dark:border-primary/25 dark:bg-primary/[0.08] relative mt-3 flex-row items-start gap-3 border p-3.5 ring-0 transition-colors duration-150",
                    available &&
                        "hover:border-primary/40 hover:bg-primary/[0.08] focus-within:border-primary/40 focus-within:bg-primary/[0.08] dark:hover:bg-primary/[0.12] dark:focus-within:bg-primary/[0.12] cursor-pointer"
                )}
            >
                {!recoverable && (
                    <button
                        type="button"
                        data-testid="template-render-open"
                        aria-label={
                            documentName
                                ? `Open ${documentName}`
                                : "Open filled document"
                        }
                        onClick={onOpen}
                        disabled={loading}
                        className="focus-visible:ring-ring/50 absolute inset-0 z-0 rounded-xl outline-none focus-visible:ring-3 focus-visible:ring-inset"
                    />
                )}
                <IconTile
                    className={cn(
                        completedIconClass,
                        !recoverable && "pointer-events-none relative z-10"
                    )}
                >
                    {completedIcon}
                </IconTile>
                <div
                    className={cn(
                        "relative z-10 min-w-0 flex-1",
                        !recoverable && "pointer-events-none"
                    )}
                >
                    {recoverable ? (
                        <div
                            data-testid="template-render-recoverable"
                            className="min-w-0"
                        >
                            <Title>
                                {documentName ?? "Document unavailable"}
                            </Title>
                            <Detail>
                                We couldn't load this document. Try again to
                                refresh it.
                            </Detail>
                            <Button
                                type="button"
                                variant="outline"
                                data-testid="template-render-resolve"
                                onClick={onResolve}
                                disabled={isResolving}
                                aria-busy={isResolving}
                                className="mt-3"
                            >
                                <RefreshCw
                                    data-icon="inline-start"
                                    className={cn(
                                        isResolving &&
                                            "animate-spin motion-reduce:animate-none"
                                    )}
                                />
                                Try again
                            </Button>
                        </div>
                    ) : (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                            <div className="min-w-0 flex-1 basis-40">
                                {loading ? (
                                    <>
                                        <Skeleton
                                            data-testid="template-render-filename-loading"
                                            className="h-[1.125rem] w-4/5 max-w-72"
                                        />
                                        <p className="text-muted-foreground mt-1 text-xs leading-tight">
                                            {fileTypeLabel}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <Title>
                                            <span
                                                className="block truncate"
                                                title={
                                                    documentName ?? undefined
                                                }
                                            >
                                                {documentName ??
                                                    "Filled document"}
                                            </span>
                                        </Title>
                                        <p className="text-muted-foreground mt-1 text-xs leading-tight">
                                            {fileTypeLabel}
                                        </p>
                                    </>
                                )}
                            </div>
                            <div className="pointer-events-auto relative z-20 flex shrink-0 items-center">
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    data-testid="template-render-download"
                                    onClick={onDownload}
                                    disabled={loading || isDownloading}
                                    aria-busy={isDownloading}
                                >
                                    {isDownloading ? (
                                        <LoaderCircle
                                            data-icon="inline-start"
                                            className="animate-spin motion-reduce:animate-none"
                                        />
                                    ) : (
                                        <Download data-icon="inline-start" />
                                    )}
                                    Download
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </Card>
        );
    }

    if (status === "skipped") {
        return (
            <Card
                data-testid="template-render-card"
                role="group"
                aria-label="Filled document"
                className="mt-3 flex-row items-start gap-3 border p-3.5 ring-0"
            >
                <IconTile className="bg-muted text-muted-foreground">
                    <Ban className="size-[1.125rem]" />
                </IconTile>
                <div className="min-w-0 flex-1">
                    <Title>Nothing to fill here</Title>
                    <Detail testId="template-render-skipped">
                        This conversation didn't have anything that maps to the
                        template. Add a few details and try again.
                    </Detail>
                    <Button
                        type="button"
                        variant="ghost"
                        data-testid="template-render-retry"
                        onClick={onRetry}
                        disabled={isRetrying}
                        aria-busy={isRetrying}
                        className="text-primary hover:text-primary mt-1 -ml-2"
                    >
                        <RotateCcw data-icon="inline-start" />
                        Try again
                    </Button>
                </div>
            </Card>
        );
    }

    const failureCode = render.failure_code;
    const adminCopy = isAdminActionCode(failureCode)
        ? ADMIN_ACTION_COPY[failureCode]
        : null;
    const adminAction = adminCopy !== null;
    const nonRetryableCopy = isNonRetryableFailureCode(failureCode)
        ? NON_RETRYABLE_FAILURE_COPY[failureCode]
        : null;
    const failureTreatment = nonRetryableCopy
        ? nonRetryableCopy.treatment
        : adminAction
          ? "admin-action"
          : "retryable";
    const needsAttention = adminAction || nonRetryableCopy !== null;

    return (
        <Card
            data-testid="template-render-card"
            role="group"
            aria-label="Filled document"
            className={cn(
                "mt-3 flex-row items-start gap-3 border p-3.5 ring-0",
                needsAttention ? "border-amber-500/30" : "border-destructive/25"
            )}
        >
            <IconTile
                className={
                    needsAttention
                        ? "bg-amber-500/10 text-amber-700 dark:text-amber-300"
                        : "bg-destructive/10 text-destructive"
                }
            >
                {needsAttention ? (
                    <Info className="size-[1.1875rem]" />
                ) : (
                    <AlertTriangle className="size-[1.1875rem]" />
                )}
            </IconTile>
            <div
                data-testid="template-render-failed"
                data-failure-code={failureCode ?? ""}
                data-failure-treatment={failureTreatment}
                className="min-w-0 flex-1"
            >
                <Title>
                    {nonRetryableCopy?.title ??
                        (adminAction
                            ? "This template needs attention"
                            : "We couldn't fill this document")}
                </Title>
                <Detail>
                    {nonRetryableCopy?.detail ??
                        (adminAction
                            ? adminCopy
                            : "Something went wrong partway through. Your conversation is safe — try again to pick up where it left off.")}
                </Detail>
                {adminAction && (
                    <span
                        data-testid="template-render-admin-hint"
                        className="sr-only"
                    >
                        Admin action required
                    </span>
                )}
                {nonRetryableCopy === null && (
                    <Button
                        type="button"
                        variant={adminAction ? "outline" : "default"}
                        data-testid="template-render-retry"
                        onClick={onRetry}
                        disabled={isRetrying}
                        aria-busy={isRetrying}
                        className="mt-3"
                    >
                        <RotateCcw data-icon="inline-start" />
                        Try again
                    </Button>
                )}
            </div>
        </Card>
    );
};

export interface TemplateRenderCardProps {
    conversationId: number;
    messageId: number;
}

export const TemplateRenderCard: FC<TemplateRenderCardProps> = ({
    conversationId,
    messageId,
}) => {
    const { data: renders } = useTemplateRenders(conversationId);
    const { openDocumentById } = useDocumentViewer();
    const retry = useRetryRender(conversationId);
    const download = useOutputDocumentDownload();
    const render = renders?.find((item) => item.chat_message_id === messageId);
    const completedDocumentId =
        render?.status === "completed" ? render.document_id : null;
    const [removedDocumentId, setRemovedDocumentId] = useState<number | null>(
        null
    );
    const observedLiveRenderIds = useRef(new Set<number>());
    const autoOpenedRenderIds = useRef(new Set<number>());
    const documentQuery = useQuery({
        ...documentInfoQueryOptions(completedDocumentId!),
        enabled: completedDocumentId !== null,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (
            render?.status !== "completed" ||
            render.document_id !== removedDocumentId
        ) {
            setRemovedDocumentId(null);
        }
    }, [removedDocumentId, render?.document_id, render?.status]);

    const openRenderDocument = useCallback(
        async (documentId: number, signal?: AbortSignal) => {
            const outcome = await openDocumentById(
                documentId,
                undefined,
                signal
                    ? { origin: "template-render-card", signal }
                    : { origin: "template-render-card" }
            );
            if (outcome.type === "not-found") {
                setRemovedDocumentId(documentId);
            }
        },
        [openDocumentById]
    );

    const renderId = render?.id ?? null;
    const renderStatus = render?.status ?? null;
    const renderDocumentId = render?.document_id ?? null;
    const renderFileType = render?.file_type?.toLowerCase() ?? null;

    useEffect(() => {
        if (renderId === null || renderStatus === null) return;
        if (LIVE_STATUSES.has(renderStatus)) {
            observedLiveRenderIds.current.add(renderId);
            return;
        }
        if (
            renderStatus !== "completed" ||
            renderFileType !== "pdf" ||
            renderDocumentId === null ||
            !observedLiveRenderIds.current.has(renderId) ||
            autoOpenedRenderIds.current.has(renderId)
        ) {
            return;
        }
        const controller = new AbortController();
        autoOpenedRenderIds.current.add(renderId);
        void openRenderDocument(renderDocumentId, controller.signal);
        return () => controller.abort();
    }, [
        openRenderDocument,
        renderDocumentId,
        renderFileType,
        renderId,
        renderStatus,
    ]);

    if (!render || render.status === "superseded") {
        return null;
    }

    const metadataRemoved =
        render.status === "completed" &&
        (render.document_id === null ||
            removedDocumentId === render.document_id ||
            isAxiosNotFoundError(documentQuery.error));
    const metadataRecoverable =
        render.status === "completed" &&
        !metadataRemoved &&
        (documentQuery.isError ||
            (documentQuery.data !== undefined &&
                !documentQuery.data.presigned_url));
    let documentAvailability: DocumentAvailability = "available";
    if (metadataRemoved) {
        documentAvailability = "removed";
    } else if (metadataRecoverable) {
        documentAvailability = "recoverable";
    } else if (render.status === "completed" && documentQuery.isPending) {
        documentAvailability = "loading";
    }

    const handleOpen = () => {
        const documentId = render.document_id;
        if (documentId === null) return;
        void openRenderDocument(documentId);
    };

    const handleDownload = () => {
        if (render.document_id === null) return;
        const documentId = render.document_id;
        download.mutate(documentId, {
            onError: (error) => {
                if (isOutputDocumentMetadataNotFoundError(error)) {
                    setRemovedDocumentId(error.documentId);
                }
            },
        });
    };

    const handleRetry = () => {
        setRemovedDocumentId(null);
        retry.mutate(render.id);
    };

    return (
        <TemplateRenderCardView
            render={render}
            documentName={documentQuery.data?.document_name ?? null}
            documentAvailability={documentAvailability}
            isDownloading={download.isPending}
            isResolving={documentQuery.isFetching}
            isRetrying={retry.isPending}
            onDownload={handleDownload}
            onOpen={handleOpen}
            onResolve={() => void documentQuery.refetch()}
            onRetry={handleRetry}
        />
    );
};
